import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Radio, Wifi, WifiOff, Monitor, Eye, Users, Clock, Play, Pause, Maximize, Volume2, VolumeX, AlertTriangle, CheckCircle, Settings, ChevronRight, BarChart3 } from 'lucide-react';

const MOCK_STREAMS = [
  {
    id: 1,
    title: 'Content Protection Live Feed — YouTube',
    platform: 'YouTube',
    color: '#FF0000',
    status: 'live',
    viewers: 1247,
    uptime: '2h 14m',
    detections: 3,
    thumbnail: null,
  },
  {
    id: 2,
    title: 'TikTok Monitoring — Audio Fingerprint',
    platform: 'TikTok',
    color: '#00F0FF',
    status: 'live',
    viewers: 843,
    uptime: '1h 38m',
    detections: 7,
    thumbnail: null,
  },
  {
    id: 3,
    title: 'Instagram Reels Scanner',
    platform: 'Instagram',
    color: '#E4405F',
    status: 'live',
    viewers: 562,
    uptime: '45m',
    detections: 2,
    thumbnail: null,
  },
  {
    id: 4,
    title: 'Twitter/X Media Monitor',
    platform: 'Twitter/X',
    color: '#1DA1F2',
    status: 'idle',
    viewers: 0,
    uptime: '--',
    detections: 0,
    thumbnail: null,
  },
];

const MOCK_ALERTS = [
  { id: 1, time: '2m ago', msg: 'Audio match detected on TikTok — 94% confidence', severity: 'high', platform: 'TikTok' },
  { id: 2, time: '8m ago', msg: 'Video repost flagged on YouTube — @ContentStealer', severity: 'high', platform: 'YouTube' },
  { id: 3, time: '15m ago', msg: 'Image repost detected on Instagram — #stolenart', severity: 'medium', platform: 'Instagram' },
  { id: 4, time: '22m ago', msg: 'Audio fingerprint match — SoundCloud user remix', severity: 'medium', platform: 'SoundCloud' },
  { id: 5, time: '34m ago', msg: 'Bulk distribution alert — Telegram channel detected', severity: 'critical', platform: 'Telegram' },
  { id: 6, time: '1h ago', msg: 'TikTok video using licensed beat without attribution', severity: 'high', platform: 'TikTok' },
];

const SEVERITY_STYLES = {
  critical: { bg: 'rgba(220,38,38,0.08)', color: '#DC2626', dot: '#DC2626' },
  high: { bg: 'rgba(255,0,229,0.06)', color: '#FF00E5', dot: '#FF00E5' },
  medium: { bg: 'rgba(245,158,11,0.06)', color: '#F59E0B', dot: '#F59E0B' },
};

export default function StreamingDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedStream, setSelectedStream] = useState(MOCK_STREAMS[0]);
  const [isMuted, setIsMuted] = useState(true);
  const [pulseCount, setPulseCount] = useState(0);

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  // Simulate live pulse
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseCount(p => p + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const liveStreams = MOCK_STREAMS.filter(s => s.status === 'live');
  const totalDetections = MOCK_STREAMS.reduce((s, st) => s + st.detections, 0);
  const totalViewers = MOCK_STREAMS.reduce((s, st) => s + st.viewers, 0);

  return (
    <div className="min-h-screen bg-texture" data-testid="streaming-dashboard">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)' }}>
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="font-label text-red-400">LIVE MONITORING</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" data-testid="streaming-title">
            <span className="text-white">Streaming </span><span className="neon-cyan">Command</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] mt-1">
            Real-time content monitoring across all connected platforms.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 animate-fade-in-up stagger-1">
          {[
            { label: 'Active Feeds', value: liveStreams.length, icon: Radio, color: '#22C55E' },
            { label: 'Live Detections', value: totalDetections, icon: AlertTriangle, color: '#FF00E5' },
            { label: 'Platforms Watched', value: MOCK_STREAMS.length, icon: Monitor, color: '#00F0FF' },
            { label: 'Alerts (1h)', value: MOCK_ALERTS.length, icon: Eye, color: '#F59E0B' },
          ].map((s, i) => (
            <div key={i} className="ch-card p-4" data-testid={`stream-stat-${i}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                style={{ background: `${s.color}12` }}>
                <s.icon size={16} style={{ color: s.color }} />
              </div>
              <p className="text-xs text-[--ch-text-secondary]">{s.label}</p>
              <p className="text-xl font-black">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Stream View */}
          <div className="lg:col-span-2 animate-fade-in-up stagger-2">
            {/* Preview Panel */}
            <div className="ch-card overflow-hidden mb-4" data-testid="main-stream-view">
              <div className="relative aspect-video overflow-hidden"
                style={{ background: 'linear-gradient(135deg, rgba(13,13,13,1), rgba(30,30,40,1))' }}>
                {/* Simulated live feed */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{
                        background: `${selectedStream.color}10`,
                        border: `2px solid ${selectedStream.color}40`,
                        boxShadow: `0 0 30px ${selectedStream.color}15`,
                      }}>
                      <Monitor size={32} style={{ color: selectedStream.color }} />
                    </div>
                    <p className="text-sm font-bold" style={{ color: selectedStream.color }}>{selectedStream.platform}</p>
                    <p className="text-xs text-[--ch-text-secondary] mt-1">Content monitoring active</p>
                  </div>
                  {/* Scan line animation */}
                  {selectedStream.status === 'live' && (
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                      <div className="w-full h-0.5 absolute animate-pulse"
                        style={{
                          top: `${(pulseCount * 7) % 100}%`,
                          background: `linear-gradient(90deg, transparent, ${selectedStream.color}40, transparent)`,
                          transition: 'top 3s linear',
                        }} />
                    </div>
                  )}
                </div>

                {/* Overlay controls */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  {selectedStream.status === 'live' && (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(220,38,38,0.9)' }}>
                      <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      <span className="text-[10px] text-white font-bold">LIVE</span>
                    </div>
                  )}
                  <div className="px-2 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <span className="text-[10px] text-white font-label">{selectedStream.title}</span>
                  </div>
                </div>

                <div className="absolute bottom-3 right-3 flex items-center gap-2">
                  <button onClick={() => setIsMuted(!isMuted)}
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.7)' }}>
                    {isMuted ? <VolumeX size={14} className="text-white" /> : <Volume2 size={14} className="text-white" />}
                  </button>
                  <button className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <Maximize size={14} className="text-white" />
                  </button>
                </div>

                {selectedStream.status === 'live' && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-3">
                    <span className="text-[10px] text-[--ch-text-secondary] flex items-center gap-1 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(0,0,0,0.7)' }}>
                      <Clock size={10} /> {selectedStream.uptime}
                    </span>
                    <span className="text-[10px] text-[--ch-text-secondary] flex items-center gap-1 px-2 py-1 rounded-full"
                      style={{ background: 'rgba(0,0,0,0.7)' }}>
                      <Eye size={10} /> {selectedStream.viewers.toLocaleString()} watching
                    </span>
                  </div>
                )}
              </div>

              {/* Stream info bar */}
              <div className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold">{selectedStream.title}</h3>
                  <p className="text-xs text-[--ch-text-secondary] mt-0.5">
                    {selectedStream.detections} detections | {selectedStream.uptime} uptime
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${selectedStream.status === 'live' ? 'bg-[--ch-success] animate-pulse' : 'bg-gray-600'}`} />
                  <span className={`text-xs font-bold ${selectedStream.status === 'live' ? 'text-[--ch-success]' : 'text-gray-500'}`}>
                    {selectedStream.status === 'live' ? 'MONITORING' : 'OFFLINE'}
                  </span>
                </div>
              </div>
            </div>

            {/* Stream Selector Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stream-grid">
              {MOCK_STREAMS.map(stream => (
                <button key={stream.id} onClick={() => setSelectedStream(stream)}
                  data-testid={`stream-${stream.id}`}
                  className={`ch-card p-3 transition-all text-left ${
                    selectedStream.id === stream.id ? 'ring-1' : 'opacity-60 hover:opacity-100'
                  }`}
                  style={selectedStream.id === stream.id ? { borderColor: `${stream.color}50`, boxShadow: `0 0 10px ${stream.color}15` } : {}}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded flex items-center justify-center"
                      style={{ background: `${stream.color}15` }}>
                      <Monitor size={12} style={{ color: stream.color }} />
                    </div>
                    {stream.status === 'live' && (
                      <div className="w-1.5 h-1.5 rounded-full bg-[--ch-success] animate-pulse" />
                    )}
                  </div>
                  <p className="text-[10px] font-bold truncate">{stream.platform}</p>
                  <p className="text-[9px] text-[--ch-text-secondary]">{stream.detections} alerts</p>
                </button>
              ))}
            </div>
          </div>

          {/* Alerts Sidebar */}
          <div className="animate-fade-in-up stagger-3">
            <div className="ch-card overflow-hidden" data-testid="alerts-panel">
              <div className="p-4 border-b border-[--ch-border] flex items-center justify-between">
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
                  <AlertTriangle size={14} className="text-[--ch-magenta]" /> Live Alerts
                </h3>
                <span className="text-xs text-[--ch-text-secondary]">{MOCK_ALERTS.length} recent</span>
              </div>
              <div className="divide-y divide-[--ch-border] max-h-[500px] overflow-y-auto">
                {MOCK_ALERTS.map(alert => {
                  const style = SEVERITY_STYLES[alert.severity];
                  return (
                    <div key={alert.id} className="p-3 hover:bg-[--ch-bg-card-hover] transition-colors cursor-pointer"
                      data-testid={`alert-${alert.id}`}>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: style.dot }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs leading-relaxed">{alert.msg}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                              style={{ background: style.bg, color: style.color }}>
                              {alert.severity.toUpperCase()}
                            </span>
                            <span className="text-[9px] text-[--ch-text-secondary]">{alert.time}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-[--ch-border]">
                <button onClick={() => navigate('/enforcement')}
                  className="w-full py-2.5 rounded-lg text-xs font-bold text-[--ch-cyan] hover:bg-[rgba(0,240,255,0.05)] transition-colors flex items-center justify-center gap-1">
                  View Enforcement Dashboard <ChevronRight size={12} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
